import { Injectable } from '@angular/core';
import { DatiIdentificativiFieldValidators } from './validators/dati-identificativi.field-validators';
import { DatiIdentificativiWarningsService } from './warnings/dati-identificativi.warnings';
import { CoopValidators } from '@core/helpers/validators.helper';
import { DatiIdentificativiConstants } from '../../../features/dati-identificativi/consts/dati-identificativi.constants';
import { datiIdentificativiFormValidators } from './validators/dati-identificativi.form-validators';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { capRegex, emailRegex, pecRegex } from '@shared/constants/regex.const';
import { setInitialTipoSezEnte } from '../../helpers/form-store.helpers';
import { DatiIdentificativiFormListeners } from './listeners/dati-identificativi.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { FormGroup } from '@angular/forms';
import { formPaths } from '@shared/constants/form-paths.const';
import { buildAderentiAFormArray, buildDichiarSostitFormArray, buildWarningFormArray } from './common/builders/dati-identificativi-form-array.builder';
import { buildSocietaPartecipateFormArray } from '../dati-bilancio/common/builders/dati-bilancio-form-array.builder';
import { buildAmministratoreUnicoFormArray } from '../amm-controllo/common/helpers/norme-form-groups.helper';

@Injectable()
export class DatiIdentificativiFormService extends BaseRevisioneFormService<DatiIdentificativiWarningsService, DatiIdentificativiFieldValidators> {
    private _certBilInfoShown = false;

    constructor(protected validators: DatiIdentificativiFieldValidators, protected warningsService: DatiIdentificativiWarningsService) {
        super(warningsService, validators);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Sezione II accertamento
            'my_SezioneII__Accertamento.my_TipoRevisioneII',
            'my_SezioneII__Accertamento.my_AnnoRevisionePerAnnualiII',
            'my_SezioneII__Accertamento.my_QualitaRappresII',
            'my_SezioneII__Accertamento.my_RappresentanteCoopCodFiscII',
            'my_SezioneII__Accertamento.my_ProvinciaSedeLegaleII',

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,

            // Scheda coop trasporto
            `${formPaths.schedaCoopTrasporto}.my_ModoDiAssociazione`,
            `${formPaths.schedaCoopTrasporto}.my_ScTrasportoObbl`,

            // Scheda altre coop
            `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaTrasporto`,
            `${formPaths.schedaAltreCoop}.my_ScAltreCoopObbl`,

            // Scheda coop edilizie
            `${formPaths.schedaCoopEdil}.my_CkIscrizAlbo`,
            `${formPaths.schedaCoopEdil}.my_ScEdilObbl`,

            // Scheda coop sociali
            `${formPaths.schedaCoopSociali}.my_ScSocObbl`,

            // Scheda coop consumo
            `${formPaths.schedaCoopConsumo}.my_ScConsumoObbl`,

            // Scheda coop PDL
            `${formPaths.schedaCoopPDL}.my_ScPDLObb`,

            // Scheda coop agricole
            `${formPaths.schedaCoopAgricole}.my_ScAgricoleObbl`,

            // Scheda coop pesca
            `${formPaths.schedaCoopPesca}.my_ScPescaObbl`,

            // Scheda coop fidi
            `${formPaths.schedaCoopFidi}.my_ScCoopFidiObbl`,

            // Scheda coop dettaglianti
            `${formPaths.schedaCoopDettaglianti}.my_ScCoopDettagliObbl`,

            // Bilancio
            `${formPaths.bilancio}.my_CkEnteTenutoCertBil`,
            `${formPaths.bilancio}.my_TotConfSociFin`,
            `${formPaths.bilancio}.my_SocietaPartecipate.my_ElSocietaPartecipate`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcA12`,

            // Amministrazione controllo
            `${formPaths.ammControllo}.my_AllAmminUnico`,
        ]);
    }

    override initListener(): void {
        new DatiIdentificativiFormListeners(this._form, this.store, this.toastrService).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_dataUltimaSincronizzazione: '',
                my_TutteLeProvinceSelezionate: '',
                my_StatoVerbale: '',
                my_ValidaCodiceISTAT: '',
                my_errSez1Rimossi: '',
                my_ValidaPosizAlboEnti: '',
                my_DBPresente: '',
                my_Warning: this.fb.group({
                    my_ElWarning: this.fb.array([]),
                }),

                my_SezioneII__Accertamento: this.fb.group({
                    my_TipoRevisioneII: '',
                    my_AnnoRevisionePerAnnualiII: '',
                    my_QualitaRappresII: '',
                    my_RappresentanteCoopCodFiscII: '',
                    my_ProvinciaSedeLegaleII: '',
                }),
                my_SezioneI__Rilevazione: this.fb.group({
                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_AllAmminUnico: this.fb.array([]),
                    }),

                    my_SchedaCoopTrasporto: this.fb.group({
                        my_ModoDiAssociazione: '',
                        my_ScTrasportoObbl: '',
                    }),

                    my_SchedaCoopAltreCoop: this.fb.group({
                        my_CkAltreCoopAllegaSchedaTrasporto: '',
                        my_ScAltreCoopObbl: '',
                    }),

                    my_SchedaCoopEdil: this.fb.group({
                        my_CkIscrizAlbo: '',
                        my_ScEdilObbl: '',
                    }),

                    my_SchedaCoopSociali: this.fb.group({
                        my_ScSocObbl: '',
                    }),

                    my_SchedaCoopConsumo: this.fb.group({
                        my_ScConsumoObbl: '',
                    }),

                    my_SchedaCoopPDL: this.fb.group({
                        my_ScPDLObbl: '',
                    }),

                    my_SchedaCoopAgricole: this.fb.group({
                        my_ScAgricoleObbl: '',
                    }),

                    my_SchedaCoopPesca: this.fb.group({
                        my_ScPescaObbl: '',
                    }),

                    my_SchedaCoopFidi: this.fb.group({
                        my_ScCoopFidiObbl: '',
                    }),

                    my_SchedaCoopDettaglianti: this.fb.group({
                        my_ScCoopDettagliObbl: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_CkEnteTenutoCertBil: '',
                        my_TotConfSociFin: '',
                        my_SocietaPartecipate: this.fb.group({
                            my_ElSocietaPartecipate: this.fb.array([]),
                        }),
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_ContoEconomico: this.fb.group({
                            my_CoEcA12: '',
                        }),
                    }),

                    my_DatiIdentificativi: this.fb.group({
                        my_Test1: '12',
                        my_Matricola: '',
                        my_TipoIncarico: '',
                        my_CkRientranteInRegimeDerogatorio: '',
                        my_TipoAdesione: '',
                        my_TipoIncaricoEffettivo: ['', null, this.validatorsService.my_TipoIncaricoEffettivo],
                        my_Id_indirizzoSedeLegale: '',
                        my_Id_indirizzoSedeAmministrativa: '',
                        my_Id_indirizzoSedeRecapito: '',
                        my_Id_statuto: '',
                        my_Id_incaricoRevisione: '',
                        my_Id_iscrizione: '',
                        my_RegimeDerogatorioProvalenza: '',
                        my_DescRegimeDerogatorioPrevalenza: '',
                        my_Anno1: { value: '', disabled: true },
                        my_Anno2: { value: '', disabled: true },
                        my_TipoRevisione: '',
                        my_AnnoRevisionePerAnnuali: '',
                        my_DataRevisioneDal: '',
                        my_DataDichSost: '',
                        my_DichiarSostit: this.fb.group({
                            my_ElDichiarSostit: this.fb.array([]),
                        }),
                        my_ObbligoRilevazioneSedeRecapito: '',
                        my_DenominazEnte: ['', CoopValidators.required],
                        my_DenominazEntePrecaricata: '',
                        my_FileStatuto: '',
                        data_lettera_Incarico: '',
                        my_id_esito_ultima_revisione: '',
                        // sede legale
                        my_IndirizzoSedeLegale: ['', CoopValidators.required],
                        my_ProvinciaSedeLegale: ['', CoopValidators.required],
                        my_DescProvinciaSedeLegale: '',
                        my_ComuneSedeLegale: ['', CoopValidators.required],
                        my_DescComuneSedeLegale: '',
                        my_CapSedeLegale: ['', [CoopValidators.required, CoopValidators.pattern(capRegex, DatiIdentificativiConstants.ERROR_CAP_NON_VALIDO)]],
                        my_LocalitaSedeLegale: '',
                        my_SpecificaTelefonoSedeLegale: '',
                        my_SpecificaFaxSedeLegale: '',
                        my_GiorniDurataRevisione: '',
                        my_SpecificaMailSedeLegale: ['', [CoopValidators.pattern(emailRegex, DatiIdentificativiConstants.ERROR_EMAIL)]],
                        my_PECcci: '',
                        my_AltraPEC: ['', CoopValidators.pattern(pecRegex, DatiIdentificativiConstants.ERROR_ALTRAPEC_PATTERN)],
                        my_CkFunzionamPEC: ['', CoopValidators.required(DatiIdentificativiConstants.ERROR_MUTUAL_PREV_REQUIRED)],
                        my_SpecificaSitoWebSedeLegale: '',
                        my_CkIndirizzoPrincipale: ['', CoopValidators.required(DatiIdentificativiConstants.ERROR_SEDE_AMMINISTRATIVA_REQUIRED)],

                        // eventuale sede amministrativa
                        my_IndirizzoSedeAmministrativa: '',
                        my_ProvinciaSedeAmministrativa: '',
                        my_DescProvinciaSedeAmministrativa: '',
                        my_ComuneSedeAmministrativa: '',
                        my_DescComuneSedeAmministrativa: '',
                        my_CapSedeAmministrativa: '',
                        my_LocalitaSedeAmministrativa: '',
                        my_SpecificaTelefonoSedeAmministrativa: '',
                        my_SpecificaFaxSedeAmministrativa: '',
                        my_SpecificaMailSedeAmministrativa: ['', [CoopValidators.pattern(emailRegex, DatiIdentificativiConstants.ERROR_EMAIL)]],
                        my_SpecificaSitoWebSedeAmministrativa: '',

                        // sede recapito
                        my_IndirizzoSedeRecapito: '',
                        my_ProvinciaSedeRecapito: '',
                        my_DescProvinciaSedeRecapito: '',
                        my_ComuneSedeRecapito: '',
                        my_DescComuneSedeRecapito: '',
                        my_CapSedeRecapito: '',
                        my_LocalitaSedeRecapito: '',
                        my_SpecificaTelefonoSedeRecapito: '',
                        my_SpecificaFaxSedeRecapito: '',
                        my_SpecificaMailSedeRecapito: ['', [CoopValidators.pattern(emailRegex, DatiIdentificativiConstants.ERROR_EMAIL)]],
                        my_SpecificaSitoWebSedeRecapito: '',

                        my_Revisore: ['', CoopValidators.required],
                        my_NIscrizRevis: '',
                        my_IncaricRevis: ['', CoopValidators.required],
                        my_LuogoRilevazione: '',
                        my_CkPeriodicRevi: ['', CoopValidators.required(DatiIdentificativiConstants.ERROR_MUTUAL_PREV_REQUIRED)],
                        my_DataCostituz: '',
                        my_CkScadenzaSocieta: ['', CoopValidators.required],
                        my_DataTermine: '',
                        my_CodFisc: '',
                        my_CodFiscOrig: '',
                        my_PartitaIVA: '',
                        my_DataUltRev: '',
                        my_desc_esito_ultima_revisione: '',
                        my_SoggDispostoRev: '',
                        my_DescSoggDispostoRev: '',
                        my_DataUltCert: '',
                        my_CkMaiCertificataBil: '',
                        my_CodAttiv: '',
                        my_CodAttivDescriz: '',
                        my_NumREA: ['', CoopValidators.required],
                        my_CkMutualPrev: '',
                        my_PosizAlboEnti: [''],
                        my_CkImpresaSociale: '',
                        my_DataUltModStat: '',
                        my_DataUltModStatOrig: [''],
                        my_CkModStatMaiModificata: ['', CoopValidators.required(DatiIdentificativiConstants.ERROR_MUTUAL_PREV_REQUIRED)],
                        my_TipoSezEnte: '',
                        my_SezEnte: '',
                        my_DescSezEnte: '',
                        my_ckSezAlboCorretta: ['', CoopValidators.required],
                        my_AderentiA: this.fb.group({
                            my_ElAderentiA: this.fb.array([]),
                        }),

                        my_NoteAdesione: '',
                        my_RappresentanteCoopScelta: '',
                        my_RappresentanteCoopCognome: ['', CoopValidators.required],
                        my_RappresentanteCoopNome: ['', CoopValidators.required],
                        my_RappresentanteCoopCodFisc: ['', CoopValidators.required],
                        my_QualitaRappres: ['', CoopValidators.required],
                        my_DocumRappres: ['', CoopValidators.required],
                        my_AssistRappres: '',
                        my_CkDelegCompOrgano: '',
                        my_CkDelegProcura: '',
                        my_SpecificaLuogoRilevazione: '',
                        my_OggSocStatuto: '',
                        my_AttivSvoltaConcreta: '',
                        my_CkAttivEffettSvolta: '',
                        my_AttivEffettSvolta: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: datiIdentificativiFormValidators(this.store),
            }
        );
    }

    protected override beforePopulateForm(myVerbaleClone: MyVerbale): void {
        buildSocietaPartecipateFormArray(this.fb, myVerbaleClone, this._form, false);
        buildWarningFormArray(this.fb, myVerbaleClone, this._form);
        buildAderentiAFormArray(this.fb, myVerbaleClone, this._form);
        buildAmministratoreUnicoFormArray(this.fb, myVerbaleClone, this._form, false);
        buildDichiarSostitFormArray(this.fb, myVerbaleClone, this._form);
        const datiIdentificativi = myVerbaleClone?.my_SezioneI__Rilevazione?.my_DatiIdentificativi;
        setInitialTipoSezEnte(myVerbaleClone, this.store);
        const my_TipoIncaricoEffettivo = datiIdentificativi?.my_TipoIncaricoEffettivo;
        const my_Anno2 = datiIdentificativi?.my_Anno2;
        const my_Anno1 = datiIdentificativi?.my_Anno1;

        switch (my_TipoIncaricoEffettivo) {
            case '1':
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoRevisione = '- I';
                myVerbaleClone.my_SezioneII__Accertamento.my_TipoRevisioneII = '- I';
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_AnnoRevisionePerAnnuali = my_Anno1;
                myVerbaleClone.my_SezioneII__Accertamento.my_AnnoRevisionePerAnnualiII = my_Anno1;
                break;
            case '2':
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoRevisione = '- II';
                myVerbaleClone.my_SezioneII__Accertamento.my_TipoRevisioneII = '- II';
                myVerbaleClone.my_SezioneII__Accertamento.my_AnnoRevisionePerAnnualiII = my_Anno2;
                break;
            case undefined:
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoRevisione = '';
                myVerbaleClone.my_SezioneII__Accertamento.my_TipoRevisioneII = '';
                myVerbaleClone.my_SezioneII__Accertamento.my_AnnoRevisionePerAnnualiII = '';
                break;
        }
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        if (!this._certBilInfoShown && myVerbale?.my_SezioneI__Rilevazione?.my_Bilancio?.my_CkEnteTenutoCertBil === '1') {
            this._certBilInfoShown = true;
            this.toastrService.info('La cooperativa certificabile è revisionata annualmente.', null, { timeOut: 1500 });
        }
    }
}
